import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgress } from '@mui/material';
import { ReactComponent as SwitchDeviceIcon } from '../../assets/images/switch-device.svg';
import SwitchDeviceMenu, { SwitchDeviceMenuItem } from './SwitchDeviceMenu';
import EventTracker from '../../pendo/EventTracker';
import PENDO_EVENT from '../../pendo/events.constants';
import { useCall } from '../../customer-call-sdk';
import useUser from '../../hooks/useUser';
import useFeatures from '../../hooks/useFeatures';
import useDelayedFlag from '../../hooks/useDelayedFlag';
import { getDeviceId } from '../../lib/generateDeviceId';
import {
    UserDevice,
    cancelDeviceSwitch,
    getUserDevices,
    requestDeviceSwitch,
} from '../../services/device-switch.service';
import { useToast } from 'src/context/ToastContext';
import Messages from '../../config/messages.constants';

export interface SwitchDeviceControlProps {
    className?: string;
}

const Wrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const MenuAnchor = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
`;

const TriggerButton = styled.button`
    &:focus:not(:focus-visible) {
        outline: none;
    }
    .icon {
        width: 56px;
        height: 56px;
    }
    /* Keeps the icon's footprint while loading, so the control does not resize. */
    .spinner {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
`;

const CancelButton = styled.button`
    min-width: 62px;
    padding: 5px 8px;
    border-radius: 500px;
    background: #ffffff;
    color: #007d3e;
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;

    &:focus:not(:focus-visible) {
        outline: none;
    }

    &:hover {
        background: #f2f2f2;
    }

    &:active {
        background: #e0e0e0;
        color: #00401f;
    }

    &:disabled {
        cursor: default;
    }
`;

const CancelAction: React.FC<{ onCancel: () => Promise<void> }> = ({ onCancel }) => {
    const [cancelling, setCancelling] = useState(false);

    return (
        <CancelButton
            type="button"
            aria-label="Cancel Switch Request"
            disabled={cancelling}
            onClick={() => {
                setCancelling(true);
                onCancel().finally(() => setCancelling(false));
            }}
        >
            {cancelling ? <CircularProgress size={16} color="inherit" /> : 'Cancel'}
        </CancelButton>
    );
};

const toMenuItem = (device: UserDevice): SwitchDeviceMenuItem => ({
    deviceId: device.deviceId,
    deviceName:
        device.type === 'Desktop'
            ? `${device.description} on ${device.deviceMake}`
            : device.description,
    lastActiveAt: device.lastModifiedOnText,
    deviceType: device.type,
});

const SwitchDeviceControl: React.FC<SwitchDeviceControlProps> = ({ className }) => {
    const { requestId, pendingSwitchDeviceName, setPendingSwitchDeviceName } = useCall();
    const user = useUser();
    const { switchDevice } = useFeatures();
    const toast = useToast();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const isCancellingRef = useRef(false);
    const isFetchingDevicesRef = useRef(false);
    const [open, setOpen] = useState(false);
    const [devices, setDevices] = useState<SwitchDeviceMenuItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [sourceDeviceType, setSourceDeviceType] = useState('');
    const [switchingDeviceId, setSwitchingDeviceId] = useState('');
    // Delayed so a fast response closes the menu without flashing a loading state.
    const showButtonSpinner = useDelayedFlag(loading);
    const spinningDeviceId = useDelayedFlag(!!switchingDeviceId) ? switchingDeviceId : '';

    const close = useCallback(() => {
        setOpen(false);
        buttonRef.current?.focus();
    }, []);

    const handleClick = useCallback(() => {
        if (open) {
            close();
            return;
        }
        if (isFetchingDevicesRef.current) {
            return;
        }

        if (!navigator.onLine) {
            toast.error(Messages.NO_INTERNET);

            return;
        }

        isFetchingDevicesRef.current = true;
        setLoading(true);
        getUserDevices().then((result) => {
            isFetchingDevicesRef.current = false;
            setLoading(false);
            if (result.error) {
                toast.error(Messages.SWITCH_DEVICES_LOAD_ERROR);
                return;
            }
            const currentDeviceId = getDeviceId();
            const allDevices = result.data ?? [];
            const currentDeviceType =
                allDevices.find((device) => device.deviceId === currentDeviceId)?.type ?? '';

            EventTracker.trackEvent(PENDO_EVENT.SWITCH_INITIATED, {
                requestId,
                sourceDeviceType: currentDeviceType,
            });

            setSourceDeviceType(currentDeviceType);
            setDevices(
                allDevices.filter((device) => device.deviceId !== currentDeviceId).map(toMenuItem),
            );
            // Opened only once the devices are in, so the menu does not grow as they arrive.
            setOpen(true);
        });
    }, [open, close, toast, requestId]);

    const handleSelect = useCallback(
        (device: SwitchDeviceMenuItem) => {
            if (switchingDeviceId) {
                return;
            }
            setSwitchingDeviceId(device.deviceId);
            EventTracker.trackEvent(PENDO_EVENT.SWITCH_DEVICE_SELECTED, {
                requestId,
                sourceDeviceType,
                targetDeviceType: device.deviceType,
            });
            requestDeviceSwitch({
                requestId: requestId ?? '',
                userId: user?.uuid ?? '',
                fromDeviceId: getDeviceId(),
                toDeviceId: device.deviceId,
            }).then((result) => {
                setSwitchingDeviceId('');
                if (result.error) {
                    // The menu stays open so another device can be picked.
                    toast.error(result.error);
                    return;
                }
                setPendingSwitchDeviceName(device.deviceName);
                close();
            });
        },
        [
            switchingDeviceId,
            close,
            requestId,
            user,
            sourceDeviceType,
            toast,
            setPendingSwitchDeviceName,
        ],
    );

    const handleCancel = useCallback(() => {
        if (isCancellingRef.current) {
            return Promise.resolve();
        }
        isCancellingRef.current = true;

        return cancelDeviceSwitch(requestId ?? '').then((result) => {
            isCancellingRef.current = false;
            if (result.error) {
                toast.error(result.error);
                return;
            }
            EventTracker.trackEvent(PENDO_EVENT.SWITCH_CANCELED, {
                requestId,
                canceledFrom: 'source',
            });
            setPendingSwitchDeviceName('');
        });
    }, [requestId, toast, setPendingSwitchDeviceName]);

    useEffect(() => {
        if (!pendingSwitchDeviceName) {
            return;
        }

        const id = toast.success(
            Messages.SWITCH_DEVICE_REQUEST_SENT_MESSAGE(pendingSwitchDeviceName),
            {
                banner: 2,
                icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
                action: () => <CancelAction onCancel={handleCancel} />,
            },
        );

        return () => toast.close(id);
    }, [pendingSwitchDeviceName, toast, handleCancel]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        const onPointerDown = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open, close]);

    if (!switchDevice) {
        return null;
    }

    return (
        <Wrapper ref={wrapperRef} className={className}>
            {open && (
                <MenuAnchor>
                    <SwitchDeviceMenu
                        devices={devices}
                        onSelect={handleSelect}
                        switchingDeviceId={spinningDeviceId}
                    />
                </MenuAnchor>
            )}
            <TriggerButton
                ref={buttonRef}
                type="button"
                id="switch-device-control"
                onClick={handleClick}
                aria-label="Switch device"
            >
                {showButtonSpinner ? (
                    <span className="icon spinner">
                        <CircularProgress size={42} color="inherit" />
                    </span>
                ) : (
                    <SwitchDeviceIcon className="icon" />
                )}
            </TriggerButton>
        </Wrapper>
    );
};

export default SwitchDeviceControl;
